// WhatsApp in Connect (6.13, 4.9): the pieces the inbox, the approval cards and the person page share. Every action
// goes through act (lib/whatsapp_connect.ts), which checks the window and the opt-in again in the database.
import { useId, useState } from "react";
import type { Crm } from "../../lib/crm";
import { Notice } from "../../components/ui";
import { fmtDateTime } from "../../lib/format";
import { replyProblem, SOURCE_LABEL, ticks, timeLeft, urgency, type WaDraft, type WaThreadMessage } from "../../lib/whatsappInbox";

/** The 24-hour window's time left, as a chip: red under an hour, amber under four. */
export function WindowChip({ minutes }: { minutes: number | null | undefined }) {
  const m = minutes ?? 0;
  const tone = m <= 0 ? "chip-plain" : urgency(m) === "urgent" ? "chip-bad" : urgency(m) === "soon" ? "chip-warn" : "chip-ok";
  return (
    <span className={`chip ${tone}`} title="WhatsApp lets a business reply freely for 24 hours after the person's last message">
      {m > 0 ? `Window: ${timeLeft(m)}` : "Window closed"}
    </span>
  );
}

const REJECT = [
  ["tone", "Tone"],
  ["facts", "Facts"],
  ["too_pushy", "Too pushy"],
  ["timing", "Timing"],
  ["other", "Other"],
] as const;

/** One reply the assistant drafted: approve as it is, edit then approve, or reject with a reason. */
export function DraftReply({ crm, draft, open, onDone }: { crm: Crm; draft: WaDraft; open: boolean; onDone: (msg: string, tone?: "ok" | "bad") => void }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(draft.body);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const id = useId();
  const problem = editing ? replyProblem(body) : null;
  const decide = async (approve: boolean, opts: { body?: string; reject_reason?: string } = {}) => {
    setBusy(true);
    try {
      const r = await crm.decideWhatsAppReply(draft.id, approve, opts);
      onDone(approve ? (r.edited ? "Reply edited and approved. It goes out in the next send." : "Reply approved. It goes out in the next send.") : "Reply rejected.");
    } catch (e) {
      onDone((e as Error).message, "bad");
      setBusy(false);
    }
  };
  return (
    <div className="wa-draft" aria-label="Drafted reply">
      <p className="muted small">
        Drafted by the assistant {fmtDateTime(draft.created_at)}
        {draft.route === "sampled" ? " · picked for your spot check" : ""}
      </p>
      {editing ? (
        <>
          <label className="field-label" htmlFor={id}>
            Reply
          </label>
          <textarea id={id} rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
          <Notice text={problem} tone="bad" />
        </>
      ) : (
        <blockquote className="message-body pre">{draft.body}</blockquote>
      )}
      {!open && <p className="small">The 24-hour window has closed, so this reply can't go now. Send the follow-up template instead.</p>}
      <div className="inline wrap">
        {editing ? (
          <>
            <button type="button" className="btn btn-primary btn-small" disabled={busy || !!problem || !open} onClick={() => void decide(true, { body: body.trim() })}>
              Save and approve
            </button>
            <button type="button" className="btn btn-small btn-quiet" onClick={() => { setEditing(false); setBody(draft.body); }}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-primary btn-small" disabled={busy || !open} onClick={() => void decide(true)}>
              Approve and send
            </button>
            <button type="button" className="btn btn-small" disabled={busy || !open} onClick={() => setEditing(true)}>
              Edit
            </button>
            <button type="button" className="btn btn-small btn-quiet" disabled={busy} onClick={() => setRejecting((v) => !v)} aria-expanded={rejecting}>
              Reject
            </button>
          </>
        )}
      </div>
      {rejecting && !editing && (
        <div className="inline wrap" role="group" aria-label="Why reject it?">
          {REJECT.map(([key, label]) => (
            <button key={key} type="button" className="btn btn-small btn-quiet" disabled={busy} onClick={() => void decide(false, { reject_reason: key })}>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Write your own reply (inside the window); any reply the assistant drafted for them is cancelled. */
export function WriteWhatsApp({ crm, person, initial = "", onDone }: { crm: Crm; person: string; initial?: string; onDone: (msg: string, tone?: "ok" | "bad") => void }) {
  const [body, setBody] = useState(initial);
  const [busy, setBusy] = useState(false);
  const id = useId();
  const problem = body ? replyProblem(body) : null;
  return (
    <form
      className="wa-write"
      onSubmit={async (e) => {
        e.preventDefault();
        if (replyProblem(body)) return;
        setBusy(true);
        try {
          await crm.sendWhatsAppReply(person, body.trim());
          setBody("");
          onDone("Your reply is on its way.");
        } catch (err) {
          onDone((err as Error).message, "bad");
        } finally {
          setBusy(false);
        }
      }}
    >
      <label className="field-label" htmlFor={id}>
        Write your own reply
      </label>
      <textarea id={id} rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
      <Notice text={problem} tone="bad" />
      <button type="submit" className="btn btn-small" disabled={busy || !body.trim() || !!problem}>
        {busy ? "Sending…" : "Send reply"}
      </button>
    </form>
  );
}

/** Take a conversation over from the assistant, or give it back. */
export function HandOffButton({ crm, person, handedOff, onDone }: { crm: Crm; person: string; handedOff: boolean; onDone: (msg: string, tone?: "ok" | "bad") => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-small btn-quiet"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await crm.handOffWhatsApp(person, handedOff);
          onDone(handedOff ? "The assistant is answering this conversation again." : "You have this conversation now. The assistant won't draft replies to it.");
        } catch (e) {
          onDone((e as Error).message, "bad");
        } finally {
          setBusy(false);
        }
      }}
    >
      {handedOff ? "Give back to the assistant" : "Take over"}
    </button>
  );
}

/** One WhatsApp message with who sent it and, for ours, WhatsApp's ticks. */
export function WaMessage({ m }: { m: WaThreadMessage }) {
  const t = m.direction === "out" ? ticks(m.status, m.outbox_status) : null;
  return (
    <li className={`wa-msg wa-${m.direction}`}>
      <div className="tl-meta">
        <span className="tl-who">{SOURCE_LABEL[m.source]}</span>
        <time dateTime={m.at}>{fmtDateTime(m.at)}</time>
        {m.template && <span className="muted small">template</span>}
        {t?.label && (
          <span className={`wa-ticks wa-ticks-${t.tone}`} aria-label={t.label} title={t.label}>
            <span aria-hidden="true">{t.mark}</span> <span className="small">{t.label}</span>
          </span>
        )}
      </div>
      <p className="pre">{m.body}</p>
    </li>
  );
}
